import { useState } from 'react'
import { useTranslation } from 'react-i18next'

// digestMessage helper (same as Intertidal DSS)
const digestMessage = async (message: string): Promise<string> => {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

interface StagingLoginProps {
  onAuthenticated: () => void
}

export const StagingLogin: React.FC<StagingLoginProps> = ({ onAuthenticated }) => {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!password.trim()) {
      return
    }

    setIsLoading(true)

    try {
      const hashedPassword = await digestMessage(password)
      const expectedHash = import.meta.env.VITE_SITE_GATING_MATCH

      if (hashedPassword === expectedHash) {
        onAuthenticated()
      } else {
        alert(t('staging.incorrectPassword'))
      }
    } catch (error) {
      console.error('Authentication error:', error)
      alert(t('staging.authFailed'))
    } finally {
      setIsLoading(false)
      setPassword('')
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
      <div className="w-full max-w-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="text-center">
            <h1 className="mb-2 text-lg font-medium text-gray-900">
              {t('staging.passwordRequired')}
            </h1>
          </div>

          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('staging.enterPassword')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? t('staging.loggingIn') : t('staging.login')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
